// app/shared/components/typography/HTFHeading.tsx

import React from 'react';

import { cn } from '@/lib/utils';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type TextSize =
  | 'xs' | 'sm' | 'base' | 'lg' | 'xl'
  | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
type FontWeight = 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';

interface HTFHeadingPropsBase {
  as?: HeadingLevel;
  size?: TextSize;
  fontWeight?: FontWeight;
  className?: string;
}

// Discriminated union for children vs title/accentText
type HTFHeadingProps = HTFHeadingPropsBase & (
  | { children: React.ReactNode; title?: never; accentText?: never; accentColorClass?: never; } // If children is present, others are not
  | { children?: never; title: string; accentText: string; accentColorClass: string; } // If title/accent is present, children is not
);


export function HTFHeading({
  as: Element = 'h2',
  size = '3xl',
  fontWeight = 'bold',
  className,
  children,
  title,
  accentText,
  accentColorClass,
}: HTFHeadingProps) {
  const sizeClasses: Record<TextSize, string> = {
    xs: 'text-xs', sm: 'text-sm', base: 'text-base', lg: 'text-lg', xl: 'text-xl',
    '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl',
    '5xl': 'text-5xl', '6xl': 'text-6xl', '7xl': 'text-7xl',
  };
  const weightClasses: Record<FontWeight, string> = {
    normal: 'font-normal', medium: 'font-medium', semibold: 'font-semibold',
    bold: 'font-bold', extrabold: 'font-extrabold',
  };

  const renderContent = () => {
    if (title && accentText && accentColorClass) {
      const parts = title.split(`{${accentText}}`);
      return (
        <>
          {parts[0]}
          <span className={accentColorClass}>{accentText}</span>
          {parts[1]}
        </>
      );
    }
    return children; // This will be undefined if title/accentText are used, which is fine for React.
  };

  return (
    <Element
      className={cn(
        'font-heading tracking-tight',
        sizeClasses[size],
        weightClasses[fontWeight],
        className
      )}
    >
      {renderContent()}
    </Element>
  );
}